export const ASSISTANT_CONFIG = {
  // Deliberately disabled while Turnstile, privacy notice, booking questions and
  // owner transcript review remain launch gates.
  temporaryTestMode: import.meta.env.VITE_SALES_ASSISTANT_TEST_MODE === "true",
  enabled: import.meta.env.VITE_SALES_ASSISTANT_ENABLED === "true" && (Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY) || import.meta.env.VITE_SALES_ASSISTANT_TEST_MODE === "true"),
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || "",
  triggerSeconds: 15,
  automaticMinimumViewportWidth: 1024,
  oncePerSession: true,
  pauseWhenHidden: true,
  deferWhileFormFieldFocused: true,
  formBlurGraceSeconds: 3,
  resumeTabAfterEngagement: true,
  excludePatterns: [
    "^/(?:ar|admin|api|oauth|media|manus-storage|__manus__)(?:/|$)",
    "^/(?:privacy-policy|cookie-policy|404|unsubscribed)(?:/|$)",
    "^/free-ai-growth-audit/thank-you/?$",
  ],
  calendlyUrl: "https://calendly.com/modielnadi/30min",
  scoreUrl: "/free-ai-growth-audit/",
  auditUrl: "/free-ai-growth-audit/",
  trainingUrl: "/services/ai-training-for-leaders/",
} as const;

const excludePatterns = ASSISTANT_CONFIG.excludePatterns.map((pattern) => new RegExp(pattern));

/** Client labels only; facts and commercial recommendations remain server-grounded. */
export const SERVICE_LINKS: Record<string, { label: string; href: string }> = {
  "ai-visibility-audit": { label: "AI Visibility Audit", href: "/free-ai-growth-audit/" },
  "ai-citation-service": { label: "AI Citation Service", href: "/services/ai-citation-service/" },
  foundation: { label: "Foundation", href: "/pricing/" },
  growth: { label: "Growth", href: "/pricing/" },
  authority: { label: "Authority", href: "/pricing/" },
  migration: { label: "AI-indexable migration", href: "/pricing/#migration" },
  imc: { label: "Integrated Marketing Communications", href: "/services/integrated-marketing-communications/" },
  "ai-websites": { label: "AI Websites", href: "/services/ai-websites/" },
  training: { label: "AI Training for Leaders", href: "/services/ai-training-for-leaders/" },
  ppc: { label: "PPC and Performance Max", href: "/services/ppc-performance-max/" },
  abm: { label: "Account-Based Marketing", href: "/services/account-based-marketing/" },
  "seo-aeo-geo": { label: "SEO, AEO and GEO", href: "/services/seo-aeo-geo/" },
  "ai-content-operations": { label: "SEO, AEO and GEO", href: "/services/seo-aeo-geo/" },
  "ai-mode": { label: "Google AI Mode Optimization", href: "/services/google-ai-mode-optimisation/" },
  "preferred-sources": { label: "Preferred Sources Strategy", href: "/services/preferred-sources-strategy/" },
  agentic: { label: "Agentic AI Lead Generation", href: "/services/gemini-agentic-ai/" },
  strategy: { label: "AI Marketing Strategy", href: "/services/ai-marketing-strategy/" },
  governance: { label: "AI Governance", href: "/services/ai-governance/" },
  "local-presence": { label: "Local Presence Plan", href: "/services/local-presence-plan/" },
  "local-visibility": { label: "AI Visibility Plan", href: "/services/ai-visibility-plan/" },
  "local-growth": { label: "Full AI Growth Engine", href: "/services/full-ai-growth-engine/" },
  score: { label: "Free AI Visibility Score", href: "/free-ai-growth-audit/" },
};

export type AssistantPageType = "pricing" | "audit" | "score" | "form" | "local-plan" | "service" | "other";

export function normaliseAssistantPath(path: string): string {
  const rawPath = path.split("?")[0] || "/";
  return rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
}

export function isEligiblePath(path: string): boolean {
  const pathname = normaliseAssistantPath(path);
  return !/\.[a-z0-9]{1,8}$/i.test(pathname) && !excludePatterns.some((pattern) => pattern.test(pathname));
}

export function pageTypeOf(path: string): AssistantPageType {
  const pathname = normaliseAssistantPath(path);
  if (/^\/pricing\/?$/.test(pathname)) return "pricing";
  if (/^\/services\/ai-visibility-audit\/?$/.test(pathname)) return "audit";
  if (/^\/free-ai-growth-audit\/?$/.test(pathname)) return "score";
  if (/^\/(contact|start)\/?$/.test(pathname)) return "form";
  if (/^\/services\/(local-presence-plan|ai-visibility-plan|full-ai-growth-engine)\/?$/.test(pathname)) return "local-plan";
  if (/^\/services\//.test(pathname)) return "service";
  return "other";
}

export function scoreUrlWithPrefill(origin: string, website: string | undefined, email: string): string {
  const url = new URL(ASSISTANT_CONFIG.scoreUrl, origin);
  const candidate = website || (email.includes("@") ? `https://${email.split("@")[1]}` : "");
  if (candidate) url.searchParams.set("url", candidate);
  url.searchParams.set("utm_source", "sales-assistant");
  url.searchParams.set("utm_medium", "chat");
  return `${url.pathname}${url.search}`;
}

export function openerFor(pageType: AssistantPageType, serviceName?: string): { line: string; chips: string[] } {
  switch (pageType) {
    case "pricing": return { line: "What is the main marketing problem you want to solve? I can point you to the most relevant option.", chips: ["We need better AI-search visibility", "We need more qualified leads", "Our paid media is underperforming"] };
    case "audit": return { line: "What would you like to understand or improve about your AI-search visibility?", chips: ["We are not appearing in AI answers", "We need a clear gap assessment", "We need a plan to improve"] };
    case "score": return { line: "What do you want your website to do better in search and AI answers?", chips: ["Show us our main gaps", "Help us decide what to prioritize", "We want more qualified inquiries"] };
    case "form": return { line: "What are you trying to improve, and what would a useful outcome look like?", chips: ["More qualified B2B leads", "Better AI-search visibility", "A joined-up marketing plan"] };
    case "local-plan": return { line: "What would you most like to improve for your local business?", chips: ["More local inquiries", "Better local visibility", "A stronger growth plan"] };
    default: return { line: `Would you like help with AI visibility, AEO and SEO, or another challenge in ${serviceName || "your marketing"}?`, chips: ["Improve AI visibility, AEO and SEO", "Discuss PPC and paid media", "Discuss an AI website", "Discuss a strategy consultation"] };
  }
}
